<template>

    <div>
        <div class="box box-solid">
            <div class="box-header">
                <h2 class="box-title">Begroting</h2>
                <div class="box-tools">
                    <json-excel
                        :data="getJSONData"
                        :name="getExportName()"
                        v-tooltip="'Exporteren naar Excel'"
                        class="btn btn-default"
                    >
                        <i class="mdi mdi-file-excel-box"></i>
                    </json-excel>
                    <button class="btn btn-default btn-xs" @click="moveReset()" v-show="isResetable" data-tooltip="tooltip" title="Terugzetten">Reset</button>
                    <div class="btn-group btn-group-xs">
                        <button class="btn btn-default" @click="moveBackwardsYear()" data-tooltip="tooltip" title="Een jaar terug"><i class="fa fa-angle-double-left"></i></button>
                        <button class="btn btn-default" type="button">{{ currentyear }}</button>
                        <button class="btn btn-default" @click="moveForwardYear()" data-tooltip="tooltip" title="Een jaar vooruit"><i class="fa fa-angle-double-right"></i></button>
                    </div>
                    <button
                        @click="addBudgetEntry"
                        class="btn btn-box-tool"
                        v-tooltip="'Begroting element toevoegen'"
                    >
                        <i class="fa fa-plus"></i>
                        <span class="sr-only">Begroting element toevoegen</span>
                    </button>
                </div>
            </div>
            <div class="box-body no-padding table-responsive" v-if="!loading">
                <table class="table table-striped table-hover table-condensed centered-table-headers">
                    <thead>
                        <tr>
                            <th rowspan="2" colspan="2">Categorie</th>
                            <th v-for="(colspan, year) in getSpanningYears" :colspan="colspan">{{ year }}</th>
                            <th rowspan="2">Totaal</th>
                        </tr>
                        <tr>
                            <th v-for="(n, addmonth) in 12" :class="{'bg-info':isCurrentMonth(addmonth)}">{{ moment(startperiod).add(addmonth, "month").format("MMM") }}</th>
                        </tr>
                    </thead>
                    <tbody v-for="category in categories">
                        <tr v-show="category.parent === null || showCategory(category.parent)">
                            <td>
                                <a @click="toggleCategory(category)" style="color:#333;cursor:pointer;">
                                    <i v-if="!showCategory(category.uuid)" class="mdi mdi-plus-circle-outline"></i>
                                    <i v-else class="mdi mdi-minus-circle-outline"></i>
                                </a>
                            </td>
                            <td>{{ category.parent !== null ? '&ndash; ' : '' }}{{ category.name }}</td>
                            <td v-for="(n, addmonth) in 12" style="max-width:100px;width:100px;" :class="{'text-right': true, 'bg-info':isCurrentMonth(addmonth)}">
                                {{ getForCategory(category.uuid, addmonth) | toCurrency(false) }}
                            </td>
                            <td class="text-right">{{ getCategoryTotal(category.uuid) | toCurrency(false) }}</td>
                        </tr>
                        <tr v-for="budget in budgets[category.uuid]" v-show="showCategory(category.uuid)">
                            <td>
                                <a @click="editBudgetEntry(budget, category.uuid)" style="color:#333;cursor:pointer;">
                                    <i class="mdi mdi-pencil"></i>
                                </a>
                                <a @click="deleteBudgetEntry(budget, category.uuid)" style="color:#333;cursor:pointer;">
                                    <i class="mdi mdi-delete"></i>
                                </a>
                            </td>
                            <td>{{ budget.name }} <i class="mdi mdi-help-circle" v-tooltip="'Toegevoegd door ' + budget.creator"></i></td>
                            <td v-for="(n, addmonth) in 12" style="max-width:100px;width:100px;" :class="{'text-right': true, 'bg-info':isCurrentMonth(addmonth)}">
                                <span v-if="parseInt(budget.month) === (addmonth + 1)">
                                    {{ budget.amount | toCurrency(false) }}
                                </span>
                            </td>
                            <td class="text-right">{{ budget.amount | toCurrency(false) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="2" class="text-right">Total:</td>
                            <td v-for="(n, addmonth) in 12" style="max-width:100px;width:100px;" :class="{'text-right': true, 'bg-info':isCurrentMonth(addmonth)}">
                                {{ getMonthTotal(addmonth) | toCurrency(false) }}
                            </td>
                            <td class="text-right">{{ getTotal | toCurrency(false) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <div class="overlay" v-show="loading">
                <i class="fa fa-refresh fa-spin"></i>
            </div>
        </div>
        <div class="row">
            <div class="col-sm-4 col-sm-offset-4">
                <div class="box box-solid">
                    <div class="box-header">
                        <h2 class="box-title">Categorieen</h2>
                        <div class="box-tools">
                            <button @click="$refs.budgetCategoryFormModal.openModal()" class="btn btn-box-tool" v-tooltip="'Categorie toevoegen'"><i class="fa fa-plus"></i></button>
                        </div>
                    </div>
                    <div class="box-body no-padding">
                        <table class="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Categorie</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="categories.length === 0">
                                    <td colspan="2">Er zijn nog geen categorie&euml;n toegevoegd</td>
                                </tr>
                                <tr v-for="category in categories">
                                    <td>{{ category.parent !== null ? '&ndash; ' : '' }}{{ category.name }}</td>
                                    <td class="text-right">
                                        <div class="btn-group btn-group-xs">
                                            <button class="btn btn-primary" @click="editCategory(category)"><i class="mdi mdi-pencil"></i></button>
                                            <delete-button
                                                :url="category._links.delete"
                                                :name="category.name"
                                                title="Categorie"
                                                @delete-completed="removeCategory(category)"
                                                v-tooltip="'Categorie verwijderen'"
                                                :disabled="category.has_children === true"
                                            ></delete-button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="overlay" v-show="loading">
                        <i class="fa fa-refresh fa-spin"></i>
                    </div>
                </div>
            </div>
        </div>
        <business-budget-form-modal
            v-if="!loading"
            @reloadData="move(startperiod)"
            :categories="categories"
            :startperiod="startperiod"
            :add-budget-uri="links.add"
            ref="budgetFormModal"
        ></business-budget-form-modal>
        <business-budget-category-form-modal
            v-if="!loading"
            @reloadData="move(startperiod)"
            :categories="categories"
            :add-category-uri="links.categories.add"
            ref="budgetCategoryFormModal"
        ></business-budget-category-form-modal>
    </div>

</template>

<script>

import axios from 'axios';
import moment from 'moment';
import BusinessBudgetFormModal from './BudgetFormModal';
import BusinessBudgetCategoryFormModal from './BudgetCategoryFormModal';
import JsonExcel from "vue-json-excel";
import Lang from '../../../Lang';

export default {

    components: {
        BusinessBudgetFormModal,
        BusinessBudgetCategoryFormModal,
        JsonExcel,
    },

    computed: {

        currentyear() {
            return moment(this.startperiod).format('YYYY');
        },

        defaultStartperiod() {
            return moment().startOf("year");
        },

        getInvoiceTotal(){
            let total = 0;
            this.totals.forEach(tot => total += tot.total);
            return total;
        },

        getSpanningYears() {
            let years = {};
            for (let i = 0; i < 12; i++) {
                let year = moment(this.startperiod).add(i, "month").format("YYYY");
                if (!years[year]) {
                    years[year] = 0;
                }
                years[year]++;
            }
            return years;
        },

        getTotal() {
            let total = 0;
            for (let i = 0; i < 12; i++) {
                total += this.getMonthTotal(i);
            }
            return total;
        },

        isResetable() {
            if (this.startperiod === null) {
                return false;
            }
            return this.startperiod.format() !== this.defaultStartperiod.format();
        },

        getJSONData: {
            get() {
                let data = [];

                data.push([
                    '',
                    '',
                    Lang.get('global.month-names.january'),
                    Lang.get('global.month-names.february'),
                    Lang.get('global.month-names.march'),
                    Lang.get('global.month-names.april'),
                    Lang.get('global.month-names.may'),
                    Lang.get('global.month-names.june'),
                    Lang.get('global.month-names.july'),
                    Lang.get('global.month-names.august'),
                    Lang.get('global.month-names.september'),
                    Lang.get('global.month-names.october'),
                    Lang.get('global.month-names.november'),
                    Lang.get('global.month-names.december'),
                ]);

                this.categories.forEach((category) => {
                    if (this.budgets.hasOwnProperty(category.uuid)) {
                        let rows = {};

                        this.budgets[category.uuid].forEach((budget) => {
                            if (!rows.hasOwnProperty(budget.name)) {
                                rows[budget.name] = [null, null, null, null, null, null, null, null, null, null, null, null];
                            }

                            rows[budget.name][budget.month - 1] = (budget.amount / 100).toFixed(2);
                        });

                        for (const [key, value] of Object.entries(rows)) {
                            data.push([[category.name], ...[key], ...value]);
                        }
                    }
                });

                return data;
            },
        },
    },

    data() {
        return {
            budgets:[],
            categories:[],
            loading:true,
            links:{},
            moment:moment,
            startperiod:null,
            toggles:[],
            totals:[],
        }
    },

    methods: {

        addBudgetEntry() {
            if (this.categories.length === 0) {
                this.$snotify.error('Het is nog niet mogelijk om een regel toe te voegen, omdat er nog geen categorieen zijn toegevoegd.');
            } else {
                this.$refs.budgetFormModal.openModal();
            }
        },

        deleteBudgetEntry(budget, category_uuid){
            if (!confirm("Weet je zeker dat je deze regel wilt verwijderen?")) {
                return;
            }
            axios.delete(budget._links.delete)
            .then(() => {
                this.$snotify.success("De regel is verwijderd");
                let list = this.budgets[category_uuid];
                list.splice(list.findIndex(bd => bd.uuid === budget.uuid), 1);
                this.move(this.startperiod);
            })
            .catch(() => this.$snotify.error("Er is iets fout gegaan bij het verwijderen van de regel"));
        },

        editBudgetEntry(budget, category_uuid){
            this.$refs.budgetFormModal.setAsEdit(budget, moment(this.startperiod).add((budget.month - 1), "month").startOf("month"), category_uuid);
            this.$refs.budgetFormModal.openModal();
        },

        editCategory(category) {
            this.$refs.budgetCategoryFormModal.setAsEdit(category);
            this.$refs.budgetCategoryFormModal.openModal();
        },

        getData() {
            axios.get(this.budgetsUrl, {params:{year: moment(this.startperiod).format("Y")}})
            .then(resp => this.storeData(resp.data.data))
            .catch(() => this.$snotify.error("Er is een fout opgetreden bij het ophalen van de begroting categorieen"))
            .finally(() => this.loading = false);
        },

        getCategoryTotal(category_uuid) {
            let total = 0;
            if (this.totals.length === 0 || !this.totals[category_uuid]) {
                return total;
            }
            for (let i = 1; i <= 12; i++){
                if (this.totals[category_uuid] && this.totals[category_uuid][i]) {
                    total += parseInt(this.totals[category_uuid][i]);
                }
            }
            return total;
        },

        getForCategory(category_uuid, addmonth) {
            let monthnr = addmonth + 1;
            if (this.totals.length === 0 || !this.totals[category_uuid] || !this.totals[category_uuid][monthnr]) {
                return 0;
            }
            return parseInt(this.totals[category_uuid][monthnr]);
        },

        getMonthTotal(addmonth) {
            let monthnr = addmonth + 1;
            let total = 0;
            for (let category_uuid in this.totals) {
                if (this.totals.hasOwnProperty(category_uuid)) {
                    // Get the category
                    let category = this.categories.filter(cat => cat.uuid === category_uuid)[0];
                    if (category.parent === null && this.totals[category_uuid][monthnr]) {
                        total += parseInt(this.totals[category_uuid][monthnr]);
                    }
                }
            }
            return total;
        },

        isCurrentMonth(addmonth) {
            return moment().isBetween(
                moment(this.startperiod).add(addmonth, "month").startOf("month"),
                moment(this.startperiod).add(addmonth, "month").endOf("month")
            );
        },

        move(newstartperiod) {
            this.loading = true;
            this.categories = this.budgets = this.links = this.totals = [];
            this.startperiod = newstartperiod.startOf("month");
            this.getData();
        },

        moveBackwardsYear() {
            this.move(moment(this.startperiod).add(12,"hours").subtract(1, "year"));
        },

        moveForwardYear() {
            this.move(moment(this.startperiod).add(12,"hours").add(1, "year"));
        },

        moveReset() {
            this.move(this.defaultStartperiod);
        },

        removeCategory(category) {
            this.categories.splice(this.categories.findIndex(cat => cat.uuid === category.uuid), 1);
        },

        setStartperiod(startperiod) {
            this.startperiod = startperiod;
        },

        showCategory(category_uuid){
            return this.toggles.indexOf(category_uuid) !== -1;
        },

        storeData(data) {
            this.categories = data.categories;
            this.budgets = data.budgets;
            this.links = data._links;
            this.totals = data.totals;
        },

        toggleCategory(category) {
            if (!this.showCategory(category.uuid)) {
                this.toggles.push(category.uuid);
            } else {
                this.toggles.splice(this.toggles.findIndex(to => to === category.uuid), 1);
            }
        },

        getExportName() {
            return 'budgets-' + moment().format('YYYY-MM-DD-HH-mm');
        },
    },

    mounted() {
        this.setStartperiod(this.defaultStartperiod);
        this.getData();
    },

    name:"BusinessBudgets",

    props: {
        budgetsUrl: {
            required:true,
            type:String,
        },
    },

}

</script>
